import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrgAuthUi } from '../branding/AuthOrgBrand';

/**
 * Tenant-only: pick membership role inside this organization.
 * Platform signup has no Instructor product — teaching lives in an org workspace.
 */
export default function OrgJoinRoleToggle({
    value,
    onChange,
    orgName,
    disabledTeacher = false,
    className = '',
}) {
    const { t } = useTranslation();
    const { primary } = useOrgAuthUi();
    const displayOrg = orgName || t('auth.pathChooser.defaultOrgName');
    const options = [
        { id: 'student', label: t('auth.pathChooser.learner'), hint: t('auth.pathChooser.learnerHint') },
        { id: 'teacher', label: t('auth.pathChooser.instructor'), hint: t('auth.pathChooser.instructorHint') },
    ];

    return (
        <div className={`space-y-2 ${className}`}>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {t('auth.pathChooser.joiningAs', { orgName: displayOrg })}
            </p>
            <div
                className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700"
                role="radiogroup"
                aria-label={t('auth.pathChooser.roleAria')}
            >
                {options.map((opt) => {
                    const active = value === opt.id;
                    const locked = opt.id === 'teacher' && disabledTeacher;
                    return (
                        <button
                            key={opt.id}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            disabled={locked}
                            onClick={() => !locked && onChange(opt.id)}
                            className={`rounded-lg px-3 py-2.5 text-center transition-all ${
                                active
                                    ? 'bg-white dark:bg-slate-900 shadow-sm font-semibold'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                            } ${locked ? 'opacity-40 cursor-not-allowed' : ''}`}
                            style={active ? { color: primary, boxShadow: `inset 0 0 0 1.5px ${primary}` } : undefined}
                        >
                            <span className="block text-sm font-semibold">{opt.label}</span>
                            <span className="block text-[11px] mt-0.5 opacity-70 font-normal">{opt.hint}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/** Soft CTA under personal signup — schools start a workspace instead of an “instructor account”. */
export function WorkspaceCta({ orgStart = '/organizations/start', className = '' }) {
    const { t } = useTranslation();
    const { primary } = useOrgAuthUi();
    return (
        <div
            className={`rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-800/40 px-4 py-3 ${className}`}
        >
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                {t('auth.pathChooser.schoolOrCompany')}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                {t('auth.pathChooser.workspaceBody')}
            </p>
            <Link
                to={orgStart}
                className="inline-flex mt-2.5 text-sm font-semibold hover:underline"
                style={{ color: primary }}
            >
                {t('auth.pathChooser.createWorkspace')}
            </Link>
        </div>
    );
}
